import { TextFieldProps } from "@mui/material";
import { gql } from "generated-graphql";
import { FacilityPickerFragment } from "generated-graphql/graphql";
import { forwardRef } from "react";
import { ControllerFieldState } from "react-hook-form";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import { ErrorDisplay } from "./Forms/ErrorDisplay";

gql(`
  fragment FacilityPicker on Facility {
    id
    name
    streetAddress1
  }
`);

const FACILITIES_QUERY = gql(`
  query FacilitiesPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...FacilityPicker
      }
      count
    }
  }
`);

interface FacilityPickerProps extends ControllerFieldState {
  onChange: (facility: FacilityPickerFragment | null) => void;
  defaultSearchTerm?: string;
  disabledFacilitiesById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?: FacilityPickerFragment | null;
  lazyLoad?: boolean;
  disabled?: boolean;
  textFieldProps?: TextFieldProps;
  showErrors?: boolean;
}

export const FacilityPicker = forwardRef<HTMLDivElement, FacilityPickerProps>(
  function FacilityPicker(
    {
      onChange,
      defaultSearchTerm = "",
      disabledFacilitiesById = [],
      disabledOptionLabel = "",
      label,
      value,
      error,
      lazyLoad = true,
      disabled,
      textFieldProps,
      showErrors = true,
    },
    ref
  ) {
    return (
      <>
        <AsyncAutocomplete
          ref={ref}
          query={FACILITIES_QUERY}
          variables={{}}
          initialPageSize={25}
          additionalSearchString={defaultSearchTerm}
          getItems={(data) => data.facilities.items}
          getOptionLabel={(option: FacilityPickerFragment) => {
            const name = option.name ?? "";
            return option.streetAddress1
              ? `${name} (${option.streetAddress1})`
              : name;
          }}
          placeholderLabel={label ?? "Facility"}
          noOptionsText="Search for a facility"
          onChange={(val: FacilityPickerFragment | null) => onChange(val)}
          value={value ?? null}
          error={error}
          disabled={disabled}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionDisabled={(option: FacilityPickerFragment) =>
            disabledFacilitiesById.includes(option?.id ?? "")
          }
          renderOption={(props, option: FacilityPickerFragment) => {
            const displayName = option.streetAddress1
              ? `${option.name} (${option.streetAddress1})`
              : option.name;
            return (
              <li {...props} key={option.id}>
                {displayName}
                {disabledOptionLabel &&
                  disabledFacilitiesById.includes(option?.id ?? "") && (
                    <span>&nbsp;({disabledOptionLabel})</span>
                  )}
              </li>
            );
          }}
          textFieldProps={{
            variant: "outlined",
            autoComplete: "off",
            inputProps: {
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            },
            ...textFieldProps,
          }}
          lazyLoad={lazyLoad}
          hideErrors={!showErrors}
        />
        {showErrors && <ErrorDisplay error={error} />}
      </>
    );
  }
);
